import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Menu,
  MenuItem,
  ListItemButton,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
  Home,
  Psychology,
  SentimentSatisfied,
  Tag,
  EmojiEmotions,
  SmartToy,
  ReadMore,
  Label,
  CompareArrows,
  Close,
  ArrowDropDown,
  ExpandLess,
  ExpandMore,
  Build,
  Info,
  Summarize,
  Article,
  AutoFixHigh,
  RocketLaunch,
  CleaningServices,
  Insights,
  FormatQuote,
  Quiz,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../theme/ThemeProvider';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsMenuAnchor, setToolsMenuAnchor] = useState(null);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const theme = useTheme();
  const { darkMode, toggleDarkMode } = useCustomTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toolsItems = [
    { text: 'Sentiment Analyzer', path: '/SentimentAnalyzer', icon: <SentimentSatisfied /> },
    { text: 'Hashtag Generator', path: '/HashtagsAnalyzer', icon: <Tag /> },
    { text: 'Emoji Suggester', path: '/EmojisAnalyzer', icon: <EmojiEmotions /> },
    { text: 'AI Text Detector', path: '/AITextAnalyzer', icon: <SmartToy /> },
    { text: 'Readability Checker', path: '/ReadabilityAnalyzer', icon: <ReadMore /> },
    { text: 'Text Summarizer', path: '/TextSummarizationAnalyzer', icon: <Summarize /> },
    { text: 'Keyword Extractor', path: '/KeywordExtractionAnalyzer', icon: <Label /> },
    { text: 'Semantic Similarity', path: '/SemanticSimilarityAnalyzer', icon: <CompareArrows /> },
    { text: 'AI Humanizer', path: '/HumanizerAnalyzer', icon: <AutoFixHigh /> },
    { text: 'Content Repurposer', path: '/RepurposerAnalyzer', icon: <RocketLaunch /> },
    { text: 'Smart Simplifier', path: '/SimplifierAnalyzer', icon: <CleaningServices /> },
    { text: 'Semantic SEO Entities', path: '/SEOAnalyzer', icon: <Insights /> },
    { text: 'Smart Quote Cards', path: '/QuoteAnalyzer', icon: <FormatQuote /> },
    { text: 'Flashcards & Quiz', path: '/FlashcardAnalyzer', icon: <Quiz /> },
    { text: 'Post Splitter', path: '/PostSplitter', icon: <Psychology /> },
  ];

  const mainNavigationItems = [
    { text: 'Home', path: '/', icon: <Home /> },
    { text: 'Blog', path: '/blog', icon: <Article /> },
    { text: 'About Us', path: '/about', icon: <Info /> },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setToolsMenuAnchor(null);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleToolsMenuOpen = (event) => {
    setToolsMenuAnchor(event.currentTarget);
  };

  const handleToolsMenuClose = () => {
    setToolsMenuAnchor(null);
  };

  const handleMobileToolsToggle = () => {
    setMobileToolsOpen(!mobileToolsOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isToolsActive = () => {
    return toolsItems.some(item => item.path === location.pathname);
  };

  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <img
            src="/Logo.png"
            alt="TextSimpler Logo"
            style={{
              height: '28px',
              width: 'auto',
            }}
          />
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <Close />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {/* Main Navigation Items */}
        {mainNavigationItems.map((item) => (
          <ListItem
            key={item.text}
            button
            onClick={() => handleNavigation(item.path)}
            sx={{
              backgroundColor: isActive(item.path) ? 'action.selected' : 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemIcon sx={{ color: isActive(item.path) ? 'primary.main' : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: isActive(item.path) ? 600 : 400,
                  color: isActive(item.path) ? 'primary.main' : 'inherit',
                },
              }}
            />
          </ListItem>
        ))}

        {/* Tools Dropdown */}
        <ListItemButton onClick={handleMobileToolsToggle}>
          <ListItemIcon sx={{ color: isToolsActive() ? 'primary.main' : 'inherit' }}>
            <Build />
          </ListItemIcon>
          <ListItemText
            primary="Tools"
            sx={{
              '& .MuiListItemText-primary': {
                fontWeight: isToolsActive() ? 600 : 400,
                color: isToolsActive() ? 'primary.main' : 'inherit',
              },
            }}
          />
          {mobileToolsOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>

        <Collapse in={mobileToolsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {toolsItems.map((item) => (
              <ListItem
                key={item.text}
                button
                onClick={() => handleNavigation(item.path)}
                sx={{
                  pl: 4,
                  backgroundColor: isActive(item.path) ? 'action.selected' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ color: isActive(item.path) ? 'primary.main' : 'inherit', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: isActive(item.path) ? 600 : 400,
                      color: isActive(item.path) ? 'primary.main' : 'inherit',
                      fontSize: '0.9rem',
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backdropFilter: 'blur(20px)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 0 } }}>
            {/* Logo and Brand */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              <img
                src="/Logo.png"
                alt="TextSimpler Logo"
                style={{
                  height: '24px',
                  width: 'auto',
                }}
              />

            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  onClick={() => handleNavigation('/')}
                  sx={{
                    color: isActive('/') ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive('/') ? 600 : 400,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: 'primary.main',
                    },
                  }}
                >
                  Home
                </Button>

                <Button
                  onClick={() => handleNavigation('/blog')}
                  sx={{
                    color: isActive('/blog') ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive('/blog') ? 600 : 400,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: 'primary.main',
                    },
                  }}
                >
                  Blog
                </Button>

                {/* Tools Dropdown */}
                <Button
                  onClick={handleToolsMenuOpen}
                  endIcon={<ArrowDropDown />}
                  sx={{
                    color: isToolsActive() ? 'primary.main' : 'text.secondary',
                    fontWeight: isToolsActive() ? 600 : 400,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: 'primary.main',
                    },
                  }}
                >
                  Tools
                </Button>

                <Button
                  onClick={() => handleNavigation('/about')}
                  sx={{
                    color: isActive('/about') ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive('/about') ? 600 : 400,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: 'primary.main',
                    },
                  }}
                >
                  About Us
                </Button>
              </Box>
            )}

            {/* Tools Menu Dropdown */}
            <Menu
              anchorEl={toolsMenuAnchor}
              open={Boolean(toolsMenuAnchor)}
              onClose={handleToolsMenuClose}
              MenuListProps={{
                'aria-labelledby': 'tools-button',
              }}
              PaperProps={{
                elevation: 3,
                sx: {
                  mt: 1,
                  minWidth: 250,
                  maxHeight: 500,
                  overflow: 'auto',
                },
              }}
            >
              {toolsItems.map((item) => (
                <MenuItem
                  key={item.text}
                  onClick={() => handleNavigation(item.path)}
                  selected={isActive(item.path)}
                  sx={{
                    py: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isActive(item.path) ? 'primary.main' : 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: isActive(item.path) ? 600 : 400,
                      },
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={toggleDarkMode}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'primary.main',
                  },
                }}
              >
                {darkMode ? <Brightness7 /> : <Brightness4 />}
              </IconButton>

              {isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ ml: 1 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            backgroundColor: 'background.paper',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;